"use client";

import { Skeleton, Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui";
import {
  Check,
  ChevronDown,
  Circle,
  Info,
  Loader2,
  Lock,
  Minus,
  X,
} from "lucide-react";
import Link from "next/link";
import { type ReactNode, useEffect, useState } from "react";
import { GoClockFill } from "react-icons/go";

import {
  APPLICATION_STEPS,
  type ApplicationStepKey,
  type InterviewStepStatus,
} from "@/lib/onboarding-completion";

interface InterviewStepPresentation {
  /** Collapsed chip: background/text classes and the small leading icon. */
  chipClass: string;
  chipIcon: ReactNode;
  /** Expanded row: the sub-label under the opportunity title. */
  label: string;
  labelClass: string;
  /** Expanded row: the round marker on the right. */
  markerClass: string;
  markerIcon: ReactNode;
  /**
   * Copy for the dashed divider above the interview row. `null` means the step
   * is not resolved yet, so the divider falls back to the ready/locked copy.
   */
  dividerLabel: string | null;
}

/**
 * One place that says what each interview-step state looks like.
 *
 * `COMPLETED` is the only state that reads "Approved". Everything terminal but
 * unsuccessful (screened out, not selected, expired) is visually distinct
 * from it, because telling a rejected applicant they were approved is worse
 * than any counter being one step short.
 */
function interviewStepPresentation(
  status: InterviewStepStatus,
): InterviewStepPresentation {
  switch (status) {
    case "COMPLETED":
      return {
        chipClass: "bg-muted text-foreground",
        chipIcon: <Check size={12} strokeWidth={2.5} />,
        label: "Approved",
        labelClass: "text-muted-foreground",
        markerClass: "border-[#2563eb] bg-[#2563eb]",
        markerIcon: <Check size={14} strokeWidth={3} className="text-white" />,
        dividerLabel: "Interview submitted",
      };
    case "AWAITING_REVIEW":
      return {
        chipClass: "bg-muted text-amber-600 dark:text-amber-400",
        chipIcon: <GoClockFill size={11} />,
        label: "Awaiting review",
        labelClass: "text-amber-600 dark:text-amber-400",
        markerClass: "border-amber-500 bg-amber-500",
        markerIcon: <GoClockFill size={11} className="text-white" />,
        dividerLabel: "Interview submitted",
      };
    case "SCREENED_OUT":
      return {
        chipClass: "bg-muted text-red-600",
        chipIcon: <X size={12} strokeWidth={2.5} />,
        label: "You didn't qualify for this opportunity",
        labelClass: "text-muted-foreground",
        markerClass: "border-red-500 bg-red-500",
        markerIcon: <X size={12} strokeWidth={2.5} className="text-white" />,
        dividerLabel: "Interview submitted",
      };
    case "NOT_SELECTED":
      return {
        chipClass: "bg-muted text-red-600",
        chipIcon: <X size={12} strokeWidth={2.5} />,
        label: "Not selected for this opportunity",
        labelClass: "text-muted-foreground",
        markerClass: "border-red-500 bg-red-500",
        markerIcon: <X size={12} strokeWidth={2.5} className="text-white" />,
        dividerLabel: "Interview submitted",
      };
    case "EXPIRED":
      return {
        chipClass: "bg-muted text-muted-foreground",
        chipIcon: <Minus size={12} strokeWidth={2.5} />,
        label: "Expired",
        labelClass: "text-muted-foreground",
        markerClass: "border-muted-foreground/40 bg-muted-foreground/40",
        markerIcon: <Minus size={12} strokeWidth={3} className="text-white" />,
        dividerLabel: "Interview closed",
      };
    case "PROCESSING":
      return {
        chipClass: "bg-muted text-blue-600 dark:text-blue-400",
        chipIcon: (
          <Loader2 size={12} strokeWidth={2.5} className="animate-spin" />
        ),
        label: "In progress",
        labelClass: "text-muted-foreground",
        markerClass: "border-blue-500 bg-blue-500",
        markerIcon: (
          <Loader2
            size={12}
            strokeWidth={3}
            className="animate-spin text-white"
          />
        ),
        dividerLabel: "Interview in progress",
      };
    case "NOT_STARTED":
      return {
        chipClass: "bg-muted text-muted-foreground/50",
        chipIcon: <Circle size={12} strokeWidth={2} className="opacity-40" />,
        label: "Not started",
        labelClass: "text-muted-foreground/40",
        markerClass: "border-border bg-muted",
        markerIcon: null,
        dividerLabel: null,
      };
  }
}

interface ApplicationRequirementsProps {
  completedSteps: Set<ApplicationStepKey> | null;
  isLoading: boolean;
  allComplete: boolean;
  completedCount: number;
  variant?: "panel" | "card";
  interviewStatus?: InterviewStepStatus;
  opportunityTitle?: string;
  onboardingUrl?: string;
  /** Link for an unverified ID step once onboarding no longer offers one. */
  identityVerificationUrl?: string;
}

export function ApplicationRequirements({
  completedSteps,
  isLoading,
  allComplete,
  completedCount,
  variant = "panel",
  interviewStatus = "NOT_STARTED",
  opportunityTitle,
  onboardingUrl,
  identityVerificationUrl,
}: ApplicationRequirementsProps) {
  const [open, setOpen] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isLoading && open === null) {
      setOpen(variant === "panel" ? true : !allComplete);
    }
  }, [isLoading, allComplete, variant, open]);

  const totalSteps = APPLICATION_STEPS.length + 1;
  const pct = Math.round((completedCount / totalSteps) * 100);
  const interview = interviewStepPresentation(interviewStatus);

  return (
    <div
      className={
        variant === "card"
          ? "pt-2 pb-0"
          : "border-border border-b px-5 pt-4 pb-4 sm:px-8"
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-[18px] w-24" />
            <Skeleton className="h-3.5 w-32" />
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-[6px] flex-1 rounded-full" />
            <Skeleton className="h-3.5 w-[30px]" />
          </div>
          {/* Chip placeholders matching collapsed state */}
          <div className="flex flex-wrap items-center gap-1.5">
            <Skeleton className="h-[26px] w-[82px] rounded-full" />
            <Skeleton className="h-[26px] w-[76px] rounded-full" />
            <Skeleton className="h-[26px] w-[92px] rounded-full" />
            <Skeleton className="h-[26px] w-[82px] rounded-full" />
            <Skeleton className="h-[26px] w-[44px] rounded-full" />
          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div
            className="flex cursor-pointer items-center justify-between pb-3.5"
            onClick={() => {
              setOpen((v) => !v);
            }}
          >
            <span className="text-foreground text-[15px] font-semibold tracking-[-0.01em]">
              Application
            </span>
            <div className="text-muted-foreground flex items-center gap-2.5">
              <span className="text-muted-foreground/60 text-[12.5px] font-normal">
                {completedCount} of {totalSteps} completed
              </span>
              <ChevronDown
                size={16}
                className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
              />
            </div>
          </div>

          {/* Progress bar */}
          <div className="flex items-center gap-3 pb-3">
            <div className="bg-muted h-[6px] flex-1 overflow-hidden rounded-full">
              <div
                className="h-full rounded-full bg-[#2563eb]"
                style={{
                  width: `${pct}%`,
                  transition: "width 0.4s cubic-bezier(.16,1,.3,1)",
                }}
              />
            </div>
            <span className="text-muted-foreground/60 min-w-[30px] text-right text-[12px] font-semibold">
              {pct}%
            </span>
          </div>

          {/* Collapsed: compact step chips */}
          {!open && (
            <div className="flex flex-wrap items-center gap-1.5">
              {APPLICATION_STEPS.map((step) => {
                const done = completedSteps?.has(step.key) ?? false;
                return (
                  <div
                    key={step.key}
                    className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] ${
                      done
                        ? "bg-muted text-foreground"
                        : "bg-muted text-muted-foreground/50"
                    }`}
                  >
                    {done ? (
                      <Check size={12} strokeWidth={2.5} />
                    ) : (
                      <Circle
                        size={12}
                        strokeWidth={2}
                        className="opacity-40"
                      />
                    )}
                    {step.shortLabel}
                  </div>
                );
              })}

              {/* Separator */}
              <div className="text-muted-foreground/30 px-0.5">|</div>

              {/* Interview step chip */}
              <div
                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] ${interview.chipClass}`}
              >
                {interview.chipIcon}
                <span className="max-w-[120px] truncate">
                  {opportunityTitle ?? "Interview"}
                </span>
              </div>
            </div>
          )}

          {/* Steps (expanded) */}
          {open && (
            <div className="flex flex-col gap-1">
              {APPLICATION_STEPS.map((step) => {
                const done = completedSteps?.has(step.key) ?? false;
                const href = done
                  ? undefined
                  : (onboardingUrl ??
                    (step.key === "ID_VERIFICATION"
                      ? identityVerificationUrl
                      : undefined));
                const isClickable = Boolean(href);
                const stepContent = (
                  <>
                    <div className="min-w-0">
                      <div className="text-foreground flex items-center gap-2 text-[13.5px] leading-[1.3] font-semibold">
                        {step.label}
                        {step.required && (
                          <span className="text-muted-foreground border-border bg-muted rounded border px-1.5 py-px text-[9.5px] font-medium tracking-[0.04em] uppercase">
                            Required
                          </span>
                        )}
                      </div>
                      <div
                        className={`mt-0.5 text-[12px] font-light ${done ? "text-muted-foreground" : "text-muted-foreground/40"}`}
                      >
                        {done ? "Completed" : "Not completed"}
                      </div>
                    </div>
                    <div
                      className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                        done
                          ? "border-[#2563eb] bg-[#2563eb]"
                          : "border-border bg-muted"
                      }`}
                    >
                      {done && (
                        <Check
                          size={14}
                          strokeWidth={3}
                          className="text-white"
                        />
                      )}
                    </div>
                  </>
                );

                const className = `bg-muted/50 flex items-center justify-between rounded-[10px] px-4 py-2.5 no-underline transition-colors ${
                  isClickable ? "cursor-pointer hover:bg-muted/80" : ""
                }`;

                return href ? (
                  <Link key={step.key} href={href} className={className}>
                    {stepContent}
                  </Link>
                ) : (
                  <div key={step.key} className={className}>
                    {stepContent}
                  </div>
                );
              })}

              {/* Divider between onboarding and interview */}
              <div className="my-1 flex items-center gap-3">
                <div className="border-border flex-1 border-t border-dashed" />
                <span className="text-muted-foreground/40 shrink-0 text-[10px] font-medium tracking-[0.03em] uppercase select-none">
                  {interview.dividerLabel ??
                    (allComplete
                      ? "You're ready to interview"
                      : "Complete above to unlock")}
                </span>
                <div className="border-border flex-1 border-t border-dashed" />
              </div>

              {/* Interview step */}
              {(() => {
                const isLocked = !allComplete;
                const interviewRow = (
                  <div
                    className={`bg-muted/50 flex items-center justify-between gap-3 rounded-[10px] px-4 py-2.5 transition-colors ${
                      isLocked ? "cursor-not-allowed" : ""
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-foreground flex items-center gap-2 truncate text-[13.5px] leading-[1.3] font-semibold">
                        {isLocked && (
                          <Lock
                            size={13}
                            className="text-muted-foreground/50 shrink-0"
                          />
                        )}
                        <span className="truncate">
                          {opportunityTitle ?? "Interview"}
                        </span>
                      </div>
                      <div
                        className={`mt-0.5 text-[12px] font-light ${interview.labelClass}`}
                      >
                        {interview.label}
                      </div>
                    </div>
                    <div
                      className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 transition-colors ${interview.markerClass}`}
                    >
                      {interview.markerIcon}
                    </div>
                  </div>
                );

                return isLocked ? (
                  <Tooltip>
                    <TooltipTrigger asChild>{interviewRow}</TooltipTrigger>
                    <TooltipContent>
                      Complete all required steps above first
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  interviewRow
                );
              })()}

              {/* Info note */}
              <div className="text-muted-foreground/40 flex items-start gap-2 px-1 pt-4 text-[11.5px] leading-[1.5] font-light">
                <Info
                  size={13}
                  strokeWidth={1.8}
                  className="text-muted-foreground/30 mt-px shrink-0"
                />
                All application steps are reused whenever another role requires
                the same step, so you never have to repeat the same interview
                twice.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
